import React, { useState, useEffect, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { useUI } from '../context/UIContext';
import {
    FiSearch, FiPlus, FiMinus, FiTrash2, FiPrinter,
    FiEdit3, FiShoppingBag, FiAlertCircle, FiFileText, FiX, FiWifiOff
} from 'react-icons/fi';
import Receipt from '../components/POS/Receipt';
import { SyncManager } from '../utils/SyncManager';
import './POSPage.css';

const POSPage = () => {
    const { user } = useAuth();
    const { showToast, confirm, prompt, isOffline } = useUI();
    const [categories, setCategories] = useState([]);
    const [menuItems, setMenuItems] = useState([]);
    const [cart, setCart] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [orderLoading, setOrderLoading] = useState(false);
    const [completedOrder, setCompletedOrder] = useState(null);
    const [sessionId, setSessionId] = useState(null); // Added state
    const [isShopOpen, setIsShopOpen] = useState(false);

    // Drafts State
    const [showDraftsModal, setShowDraftsModal] = useState(false);
    const [draftOrders, setDraftOrders] = useState([]);
    const [nextOrderNumber, setNextOrderNumber] = useState(null);

    const receiptRef = useRef();
    const handlePrint = useReactToPrint({
        content: () => receiptRef.current,
    });

    // Fetch menu data
    useEffect(() => {
        fetchData();
    }, [isOffline]); // Retry fetching when back online

    const fetchData = async () => {
        setLoading(true);
        try {
            // Try to load from API if online
            if (!isOffline) {
                const [categoriesRes, menuRes, sessionRes, nextNumberRes] = await Promise.all([
                    api.get('/inventory/categories'),
                    api.get('/orders/menu'),
                    api.get('/sessions/current'),
                    api.get('/orders/next-number')
                ]);

                setCategories(categoriesRes.data);
                setMenuItems(menuRes.data);
                setIsShopOpen(sessionRes.data.isOpen);
                setSessionId(sessionRes.data.session?.id); // Set session ID
                setNextOrderNumber(nextNumberRes.data.orderNumber);

                // Cache for offline use
                await SyncManager.cacheMenu(categoriesRes.data, menuRes.data);
                SyncManager.cacheSession(sessionRes.data); // Cache session status
            } else {
                throw new Error('Offline mode');
            }
        } catch (error) {
            console.log('Fetching from cache due to error or offline mode');
            try {
                const { categories, menuItems } = await SyncManager.getCachedMenu();
                if (categories && menuItems) {
                    setCategories(categories);
                    setMenuItems(menuItems);
                    // Estimate next order number if unknown
                    if (!nextOrderNumber) setNextOrderNumber('OFF-0000');

                    // Check cached session status
                    const cachedSession = await SyncManager.getCachedSession();
                    if (cachedSession) {
                        setIsShopOpen(cachedSession.isOpen);
                        setSessionId(cachedSession.session?.id || null); // Set session ID from cache
                    } else {
                        // Default to CLOSED if unknown (Fail-Safe)
                        setIsShopOpen(false);
                        showToast('Offline functionality limited: Shop status unknown', 'warning');
                    }
                }
            } catch (cacheError) {
                console.error('Failed to load cached menu:', cacheError);
            }
        } finally {
            setLoading(false);
        }
    };

    // Fetch drafts
    const fetchDrafts = async () => {
        try {
            const res = await api.get('/orders?status=draft');
            setDraftOrders(res.data);
        } catch (error) {
            console.error('Failed to fetch drafts:', error);
        }
    };

    // Open/Close Drafts Modal
    const toggleDraftsModal = () => {
        if (!showDraftsModal) fetchDrafts();
        setShowDraftsModal(!showDraftsModal);
    };

    // Resume Draft (Cancel & Load)
    const handleResumeDraft = async (draft) => {
        if (cart.length > 0) {
            const confirmed = await confirm('Current cart will be replaced. Continue?', 'Resume Draft');
            if (!confirmed) return;
        }

        try {
            // Cancel the draft order to release inventory
            await api.post(`/orders/${draft.id}/cancel`);

            // Function to fetch order items details including inventoryId
            const res = await api.get(`/orders/${draft.id}`);
            const items = res.data.items.map(item => ({
                id: item.inventory_id,
                name: item.item_name,
                price: item.unit_price,
                quantity: item.quantity,
                notes: item.notes || '',
                is_orderable: true // Assuming active since we just released stock
            }));

            setCart(items);
            setShowDraftsModal(false);
            fetchData(); // Refresh stock
            showToast('Draft resumed', 'success');
        } catch (error) {
            console.error('Resume draft error:', error);
            showToast('Failed to resume draft', 'error');
        }
    };

    // Discard Draft
    const handleDiscardDraft = async (draftId) => {
        const confirmed = await confirm('Are you sure you want to discard this draft?', 'Discard Draft');
        if (!confirmed) return;
        try {
            await api.post(`/orders/${draftId}/cancel`);
            fetchDrafts();
            fetchData(); // Refresh stock
            showToast('Draft discarded', 'success');
        } catch (error) {
            showToast('Failed to discard draft', 'error');
        }
    };

    // Filter items by category and search
    const filteredItems = menuItems.filter(item => {
        const matchesCategory = !selectedCategory || item.category_id === selectedCategory;
        const matchesSearch = !searchQuery ||
            item.name.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesCategory && matchesSearch;
    });

    // Add item to cart
    const addToCart = (item) => {
        if (!item.is_orderable) return;

        setCart(prev => {
            const existing = prev.find(i => i.id === item.id);
            if (existing) {
                return prev.map(i =>
                    i.id === item.id
                        ? { ...i, quantity: i.quantity + 1 }
                        : i
                );
            }
            return [...prev, { ...item, quantity: 1, notes: '' }];
        });
    };

    // Update cart item quantity
    const updateQuantity = (itemId, delta) => {
        setCart(prev => {
            return prev.map(item => {
                if (item.id === itemId) {
                    const newQty = item.quantity + delta;
                    return newQty > 0 ? { ...item, quantity: newQty } : item;
                }
                return item;
            }).filter(item => item.quantity > 0);
        });
    };

    // Remove item from cart
    const removeFromCart = (itemId) => {
        setCart(prev => prev.filter(item => item.id !== itemId));
    };

    // Update item notes
    const updateNotes = (itemId, notes) => {
        setCart(prev => prev.map(item =>
            item.id === itemId ? { ...item, notes } : item
        ));
    };

    // Calculate totals
    const subtotal = cart.reduce((sum, item) => sum + (parseFloat(item.price) * item.quantity), 0);
    const taxRate = user?.branch?.tax_rate || 16;
    const taxAmount = (subtotal * taxRate) / 100;
    const total = subtotal + taxAmount;

    // Place order
    const placeOrder = async (status = 'completed') => {
        if (cart.length === 0) return;

        setOrderLoading(true);
        try {
            const orderData = {
                // ... (fields)
                items: cart.map(item => ({
                    inventoryId: item.id,
                    quantity: item.quantity,
                    notes: item.notes,
                    itemName: item.name, // Added for offline receipt
                    unitPrice: item.price, // Added for offline receipt
                    totalPrice: item.price * item.quantity // Added for offline receipt
                })),
                status,
                paymentMethod: 'cash',
                subtotal,
                taxAmount,
                taxRate, // Added for offline receipt
                totalAmount: total,
                createdAt: new Date().toISOString(), // Added for offline receipt
                orderNumber: isOffline ? `OFF-${Date.now().toString().slice(-4)}` : nextOrderNumber,
                branch: user.branch,
                branchId: user.branch_id || user.branch?.id, // Ensure branchId is sent
                sessionId: sessionId || null, // Include session ID for offline orders
                businessDate: SyncManager.getBusinessDate ? SyncManager.getBusinessDate() : new Date().toISOString().split('T')[0]
            };

            // If offline, save directly
            if (isOffline) {
                await SyncManager.saveOfflineOrder(orderData);
                setCompletedOrder(orderData); // Set for receipt
                showToast('Order saved locally (Offline)', 'warning');
            } else {
                try {
                    const response = await api.post('/orders', orderData);
                    setCompletedOrder(response.data);

                    // Prime the cache for this order and reports so they are available offline immediately
                    if (response.data && response.data.id) {
                        const today = new Date().toISOString().slice(0, 10);
                        const startOfMonth = new Date();
                        startOfMonth.setDate(1);
                        const startStr = startOfMonth.toISOString().slice(0, 10);

                        // Handle Branch Query for Super Admins to match ReportsPage URL
                        // Note: POSPage usually defaults to the user's branch or first active.
                        // We try to match the likely selectedBranch.
                        const branchId = user?.branch_id || user?.branch?.id;
                        // In ReportsPage, branchQuery is only added if isSuperAdmin is true.
                        // We need access to isSuperAdmin here. 
                        // Assuming 'user.role' check is sufficient if isSuperAdmin context variable isn't available in this scope yet (we need to update destructuring).
                        const isUserSuperAdmin = user?.role === 'super_admin';
                        const branchQuery = (isUserSuperAdmin && branchId) ? `&branchId=${branchId}` : '';

                        // Ensure we have session ID to update granular session details
                        let currentSessionId = sessionId;
                        if (!currentSessionId) {
                            try {
                                const sessRes = await api.get('/sessions/current');
                                currentSessionId = sessRes.data.session?.id;
                            } catch (e) {
                                console.warn("Failed to fetch current session ID for caching", e);
                            }
                        }

                        // Helper to append branch ID intelligently
                        const getBranchSuffix = (hasParams) => {
                            if (isUserSuperAdmin && branchId) {
                                return hasParams ? `&branchId=${branchId}` : `?branchId=${branchId}`;
                            }
                            return '';
                        };

                        const urlsToCache = [
                            `/orders/${response.data.id}`,
                            // Reports Page: Daily Sales (Has date param)
                            `/reports/daily-sales?date=${today}${getBranchSuffix(true)}`,
                            // Reports Page: Sessions (No params initially)
                            `/reports/sessions${getBranchSuffix(false)}`,
                            // Reports Page: Monthly (Has params)
                            `/reports/daily-sales?startDate=${startStr}&endDate=${today}${getBranchSuffix(true)}`,

                            // Dashboard: Sales Widget (No params - THIS WAS THE BUG)
                            `/reports/daily-sales${getBranchSuffix(false)}`,
                            // Dashboard: Recent Orders (Has params)
                            `/orders?status=completed${getBranchSuffix(true)}`
                        ];

                        if (currentSessionId) {
                            // Session Details (Has params)
                            urlsToCache.push(`/reports/daily-sales?sessionId=${currentSessionId}${getBranchSuffix(true)}`);
                        } else {
                            console.warn("Skipping session detail cache - No Session ID found");
                        }

                        console.log("Priming cache for URLs:", urlsToCache);

                        Promise.all(urlsToCache.map(url => api.get(url)))
                            .then(() => console.log(`Cached order ${response.data.id} and reports for offline access`))
                            .catch(err => console.warn("Failed to cache new order/reports:", err));
                    }

                    showToast(status === 'draft' ? 'Order saved as draft' : 'Order placed successfully', 'success');
                } catch (error) {
                    // Start of fallback logic
                    if (!navigator.onLine) {
                        await SyncManager.saveOfflineOrder(orderData);
                        setCompletedOrder(orderData);
                        showToast('Connection lost. Order saved locally.', 'warning');
                        setIsOffline(true);
                    } else {
                        throw error;
                    }
                }
            }

            if (status === 'completed') {
                // Ensure the receipt component has time to render with new data
                setTimeout(() => {
                    if (receiptRef.current) {
                        handlePrint();
                    } else {
                        console.error('Print failed: receiptRef.current is null');
                        showToast('Print failed: UI not ready. Please try manually.', 'error');
                    }
                }, 300);
            }

            setCart([]);
            if (!isOffline) fetchData(); // Only refresh if online

        } catch (error) {
            showToast(error.response?.data?.message || 'Failed to place order', 'error');
        } finally {
            setOrderLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="spinner spinner-lg"></div>
            </div>
        );
    }

    return (
        <div className="pos-page">
            {/* Menu Section */}
            <div className="pos-menu">
                <div className="pos-header">
                    <h2>Point of Sale (POS)</h2>
                    {isOffline && (
                        <div style={{
                            background: '#FEEBC8', color: '#744210', padding: '0.25rem 0.75rem',
                            borderRadius: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem',
                            fontSize: '0.85rem', fontWeight: 600
                        }}>
                            <FiWifiOff /> Offline Mode
                        </div>
                    )}
                    <div style={{ display: 'flex', gap: '1rem' }}>
                        <button
                            className="btn btn-outline"
                            onClick={toggleDraftsModal}
                            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                        >
                            <FiFileText /> Drafts
                        </button>
                        <div className="pos-search">
                            <FiSearch className="search-icon" />
                            <input
                                type="text"
                                placeholder="Search in products"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="form-input"
                            />
                        </div>
                    </div>
                </div>

                {/* Categories */}
                <div className="category-tabs">
                    <button
                        className={`category-tab ${!selectedCategory ? 'active' : ''}`}
                        onClick={() => setSelectedCategory(null)}
                    >
                        Show All
                    </button>
                    {categories.map(cat => (
                        <button
                            key={cat.id}
                            className={`category-tab ${selectedCategory === cat.id ? 'active' : ''}`}
                            onClick={() => setSelectedCategory(cat.id)}
                        >
                            {cat.name}
                        </button>
                    ))}
                </div>

                {/* Products Grid */}
                <div className="products-grid">
                    {filteredItems.map(item => (
                        <div
                            key={item.id}
                            className={`product-card ${!item.is_orderable ? 'disabled' : ''}`}
                            onClick={() => addToCart(item)}
                        >
                            <div className="product-image">
                                {item.image_url ? (
                                    <img src={item.image_url} alt={item.name} />
                                ) : (
                                    <div className="product-placeholder">🍔</div>
                                )}
                                {!item.is_orderable && (
                                    <div className="out-of-stock-badge">
                                        <FiAlertCircle /> Out of Stock
                                    </div>
                                )}
                            </div>
                            <div className="product-info">
                                <h4 className="product-name">{item.name}</h4>
                                <p className="product-price">Rs. {parseFloat(item.price).toFixed(2)}</p>
                            </div>
                            {item.is_orderable && (
                                <button className="add-btn">
                                    <FiPlus />
                                </button>
                            )}
                        </div>
                    ))}
                    {filteredItems.length === 0 && (
                        <div className="no-products">
                            <FiShoppingBag size={48} />
                            <p>No products found</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Cart Section */}
            <div className="pos-cart">
                <div className="cart-header">
                    <FiSearch className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search in Existing"
                        className="form-input"
                    />
                </div>

                <div className="cart-title">
                    <FiShoppingBag />
                    <span>Order #{cart.length > 0 ? nextOrderNumber : '--'}</span>
                </div>

                {/* Cart Items */}
                <div className="cart-items">
                    {cart.length === 0 ? (
                        <div className="cart-empty">
                            <FiShoppingBag size={40} />
                            <p>Your cart is empty</p>
                            <span>Click on items to add them</span>
                        </div>
                    ) : (
                        cart.map(item => (
                            <div key={item.id} className="cart-item">
                                <div className="cart-item-header">
                                    <h4>{item.name}</h4>
                                    <button
                                        className="remove-btn"
                                        onClick={() => removeFromCart(item.id)}
                                    >
                                        <FiTrash2 />
                                    </button>
                                </div>
                                <div className="cart-item-price">
                                    <span className="unit-price">Rs. {parseFloat(item.price).toFixed(2)}</span>
                                    <span className="multiply"> × </span>
                                    <span className="qty">{item.quantity}</span>
                                    <span className="equals"> = </span>
                                    <span className="line-total">Rs. {(parseFloat(item.price) * item.quantity).toFixed(2)}</span>
                                </div>
                                <div className="cart-item-controls">
                                    <div className="quantity-controls">
                                        <button onClick={() => updateQuantity(item.id, -1)}>
                                            <FiMinus />
                                        </button>
                                        <span>{item.quantity}</span>
                                        <button onClick={() => updateQuantity(item.id, 1)}>
                                            <FiPlus />
                                        </button>
                                    </div>
                                    <button
                                        className="notes-btn"
                                        onClick={async () => {
                                            const note = await prompt('Add notes for this item:', item.notes);
                                            if (note !== null) updateNotes(item.id, note);
                                        }}
                                    >
                                        <FiEdit3 /> Add Notes
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Cart Summary */}
                <div className="cart-summary">
                    <div className="summary-row">
                        <span>Sub total :</span>
                        <span>Rs. {subtotal.toFixed(2)}</span>
                    </div>
                    <div className="summary-row">
                        <span>Sales Tax ({taxRate}%) :</span>
                        <span>Rs. {taxAmount.toFixed(2)}</span>
                    </div>
                    <div className="summary-row total">
                        <span>Total :</span>
                        <span>Rs. {total.toFixed(2)}</span>
                    </div>
                </div>

                {/* Shop Closed Warning */}
                {!isShopOpen && (
                    <div className="alert alert-error" style={{ marginBottom: '1rem', padding: '0.75rem', borderRadius: '0.5rem', background: '#FED7D7', color: '#C53030', border: '1px solid #F56565' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: '500' }}>
                            <FiAlertCircle />
                            <span>Shop is closed. Please open the shop before ordering.</span>
                        </div>
                    </div>
                )}

                {/* Cart Actions */}
                <div className="cart-actions">
                    <button
                        className="btn btn-secondary"
                        onClick={() => placeOrder('draft')}
                        disabled={cart.length === 0 || orderLoading || !isShopOpen}
                    >
                        Draft
                    </button>
                    <button
                        className="btn btn-primary btn-lg"
                        onClick={() => placeOrder('completed')}
                        disabled={cart.length === 0 || orderLoading || !isShopOpen}
                    >
                        {orderLoading ? (
                            <>
                                <div className="spinner"></div>
                                Processing...
                            </>
                        ) : (
                            <>
                                <FiPrinter /> Bill & Print
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Drafts Modal */}
            {showDraftsModal && (
                <div className="modal-overlay" onClick={() => setShowDraftsModal(false)}>
                    <div className="modal" style={{ maxWidth: '600px', width: '90%' }} onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3 className="modal-title">Draft Orders</h3>
                            <button className="modal-close" onClick={() => setShowDraftsModal(false)}>
                                <FiX />
                            </button>
                        </div>
                        <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                            {draftOrders.length === 0 ? (
                                <p className="text-center text-muted">No draft orders found.</p>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                    {draftOrders.map(draft => (
                                        <div key={draft.id} className="card" style={{ padding: '1rem', border: '1px solid #e2e8f0' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                                                <h4 style={{ margin: 0 }}>{draft.order_number}</h4>
                                                <span className="text-muted">{new Date(draft.created_at).toLocaleString()}</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <div>
                                                    <p className="text-sm_ text-muted">{draft.item_count} items</p>
                                                    <p style={{ fontWeight: 600, color: 'var(--primary)' }}>Rs. {parseFloat(draft.total_amount).toFixed(2)}</p>
                                                </div>
                                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                    <button
                                                        className="btn btn-sm btn-outline btn-danger"
                                                        onClick={() => handleDiscardDraft(draft.id)}
                                                    >
                                                        Discard
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-primary"
                                                        onClick={() => handleResumeDraft(draft)}
                                                        disabled={!isShopOpen}
                                                    >
                                                        Resume
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Hidden Receipt for Printing */}
            <div style={{ display: 'none' }}>
                <Receipt ref={receiptRef} order={completedOrder} />
            </div>
        </div>
    );
};

export default POSPage;
